import { CreateGiftCardError } from './giftCardsApi';
import type { CreateGiftCardBody } from './giftCardTypes';

export const REASON_MAX_LENGTH = 500;

const DOLLARS = /^\$?(\d+)(?:\.(\d{1,2}))?$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface GiftCardFormValues {
  amount: string;
  recipientEmail: string;
  reason: string;
}

export type GiftCardFormErrors = Partial<Record<keyof GiftCardFormValues, string>>;

/** Whole cents for a dollar string like "25" or "25.50", or null when it is not a positive amount. */
export function dollarsToCents(input: string): number | null {
  const match = DOLLARS.exec(input.trim());
  if (!match) return null;
  const cents = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

export function validateGiftCardForm(values: GiftCardFormValues): GiftCardFormErrors {
  const errors: GiftCardFormErrors = {};
  if (dollarsToCents(values.amount) === null) {
    errors.amount = 'Enter an amount greater than $0, like 25 or 25.50.';
  }
  if (!EMAIL.test(values.recipientEmail.trim())) {
    errors.recipientEmail = 'Enter a valid email address.';
  }
  const reason = values.reason.trim();
  if (reason.length === 0) errors.reason = 'Enter a reason.';
  else if (reason.length > REASON_MAX_LENGTH) {
    errors.reason = `Keep the reason to ${REASON_MAX_LENGTH} characters or fewer.`;
  }
  return errors;
}

/** Only call with values that passed `validateGiftCardForm`. */
export function toCreateBody(values: GiftCardFormValues): CreateGiftCardBody {
  return {
    amountCents: dollarsToCents(values.amount) as number,
    recipientEmail: values.recipientEmail.trim(),
    reason: values.reason.trim(),
  };
}

export function createErrorMessage(err: unknown): string {
  const status = err instanceof CreateGiftCardError ? err.status : null;
  if (status === 400) return 'Some of the details are not valid. Check them and try again.';
  if (status === 403) return "You don't have permission to create gift cards.";
  if (status === 404) return 'That customer could not be found.';
  return 'Something went wrong and the gift card was not created. Try again.';
}
